import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Button,
  ButtonGroup,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandDownIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js';
import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import React, { useState } from 'react';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'src/types';
import emptyStateImage from '../assets/atlas-empty-state.svg';

interface CategoryEmptyStateProps {
  onAddCategory?: () => void;
  onSelectSpeakUp?: () => void;
  onSelectCoi?: () => void;
}

export const CategoryEmptyState: React.FC<CategoryEmptyStateProps> = ({
  onAddCategory,
  onSelectSpeakUp,
  onSelectCoi,
}) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const SplitButtonPresets = presets?.SplitButtonPresets ?? {};
  const bottomRight =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (presets as any)?.MenuPresets?.positionFromAnchorEl?.bottomRight ?? {};
  const speakUpEnabled = useIsFeatureEnabled(FeatureFlag.SPEAK_UP);
  const coiEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);
  const showDropdown = speakUpEnabled && coiEnabled;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${tokens.core.spacing[8].value} ${tokens.core.spacing[4].value}`,
      }}
      data-testid="category-empty-state"
    >
      <Box sx={{ marginBottom: '24px' }}>
        <img
          src={emptyStateImage}
          alt="No categories"
          width="160"
          height="132"
        />
      </Box>

      <Typography
        sx={{
          fontSize: '24px',
          fontWeight: tokens.semantic.fontWeight.emphasis.value,
          color: tokens.semantic.color.type.default.value,
          lineHeight: 1.3,
          marginBottom: '8px',
        }}
      >
        No categories found
      </Typography>

      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: tokens.semantic.fontWeight.regular.value,
          color: tokens.semantic.color.type.muted.value,
          lineHeight: 1.5,
          marginBottom: '24px',
        }}
      >
        There are no categories found. You can create one now.
      </Typography>

      <ButtonGroup
        aria-label="Add new category"
        data-testid="category-empty-state-add-button"
        {...SplitButtonPresets}
      >
        <Button
          variant="contained"
          onClick={onAddCategory}
          startIcon={<AddIcon aria-hidden />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add new category
        </Button>
        {showDropdown && (
          <Button
            variant="contained"
            aria-label="More options"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
              setAnchorEl(e.currentTarget)
            }
            data-testid="category-empty-state-dropdown-button"
          >
            <ExpandDownIcon aria-hidden />
          </Button>
        )}
      </ButtonGroup>
      {showDropdown && (
        <Menu
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          data-testid="category-empty-state-menu"
          {...bottomRight}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onSelectSpeakUp?.();
            }}
            data-testid="category-empty-state-speak-up"
          >
            <ListItemText>Speak up</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onSelectCoi?.();
            }}
            data-testid="category-empty-state-coi"
          >
            <ListItemText>Conflict of interest</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};
